import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FEATURE_FLAGS } from '../../consts/features';
import { GIT_PROVIDERS } from '../../consts/git';
import { useUser } from '../../hooks/useUser';
import { useWizardAnalytics } from '../../hooks/useWizardAnalytics';
import { Gitlab } from '../../logos';
import { GitlabWebhookInstallResult } from '../../network/httpClients/GitlabClient';
import { QUERY_KEYS } from '../../network/queries/consts';
import { useIsFeatureEnabledQuery } from '../../network/queries/useIsFeatureEnabledQuery';
import { ROUTES } from '../../routes/consts';
import {
  DialogTitleWithIcon,
  MultiStepDialog,
} from '../../shared/MultiStepDialog';
import { spawn } from '../../utils/translations';
import { getSteps, getTokenSteps } from './configuration';
import { GitlabInstallationContext } from './gitlabInstallationContext';
import { GitlabAuthType } from './types';
import {
  getDefaultFurthestStepIndex,
  getIntegrationsCloseTarget,
} from './utils';

export const GitlabInstallationDialog = () => {
  const [appConfigId, setAppConfigId] = useState<string>();
  const [isConnectionSuccessful, setIsConnectionSuccessful] = useState(false);
  const [webhookResult, setWebhookResult] =
    useState<GitlabWebhookInstallResult>();
  const [totalSucceededCount, setTotalSucceededCount] = useState(0);
  const [isGitlabAppConfigured, setIsGitlabAppConfigured] = useState(false);
  const [authType, setAuthType] = useState<GitlabAuthType>('oauth');

  const {
    isGitlabInstallationWizardAvailable,
    isGitlabInstallationWizardLoading,
  } = useUser();
  // Token-first (PAT) is gated behind a feature flag so it ships dark and is
  // enabled per-tenant for QA / staged rollout. Off → the Access-token option
  // renders disabled ("Coming soon"), same as the multi-tenant wizard.
  const { data: isTokenAuthFlagEnabled } = useIsFeatureEnabledQuery(
    FEATURE_FLAGS.GITLAB_TOKEN_AUTH,
  );
  // GAT ships behind its own flag and is hidden entirely when off (not "Coming
  // soon").
  const { data: isGroupTokenAuthFlagEnabled } = useIsFeatureEnabledQuery(
    FEATURE_FLAGS.GITLAB_GROUP_TOKEN_AUTH,
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const spawnGeneral = spawn('dialogs.gitlab-installation.general');
  // OAuth uses the full app-creation wizard; both token flavors (pat, gat) use
  // the short token wizard (BeforeYouBegin → ConfigureToken → EnableQodo).
  const steps = authType === 'oauth' ? getSteps() : getTokenSteps();
  const {
    onStepViewed,
    onStepNavigated,
    onCancel: trackClose,
    onWizardCompleted,
  } = useWizardAnalytics({
    gitProvider: GIT_PROVIDERS.GITLAB,
    wizardVersion: 'v1',
    steps,
  });

  const defaultFurthestStepIndex = getDefaultFurthestStepIndex(
    location.pathname,
    location.search,
    ROUTES.GITLAB_INSTALLATION_STEP_5,
    steps,
  );

  const close = () => {
    navigate(getIntegrationsCloseTarget());
  };

  const onCancel = (stepIndex: number, stepId: string) => {
    trackClose(stepIndex, stepId);
    close();
  };

  const onComplete = () => {
    onWizardCompleted();
    close();
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.INSTALLATIONS_COUNTS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GIT_INSTALLATIONS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GIT_REPOSITORIES],
    });
  };

  if (
    !isGitlabInstallationWizardAvailable &&
    !isGitlabInstallationWizardLoading
  ) {
    console.warn("You're not allowed to access this page");

    return <Navigate to={ROUTES.ROOT} />;
  }

  return (
    <GitlabInstallationContext.Provider
      value={{
        authType,
        setAuthType,
        isTokenAuthEnabled: Boolean(isTokenAuthFlagEnabled),
        isGroupTokenAuthEnabled: Boolean(isGroupTokenAuthFlagEnabled),
        appConfigId,
        setAppConfigId,
        isConnectionSuccessful,
        setIsConnectionSuccessful,
        webhookResult,
        setWebhookResult,
        totalSucceededCount,
        setTotalSucceededCount,
        isGitlabAppConfigured,
        setIsGitlabAppConfigured,
      }}
    >
      <MultiStepDialog
        // Remount when the auth type changes so the dialog's internal
        // furthestStepReached/completedSteps reset — otherwise progress made in
        // the OAuth flow would carry over to the (shorter) token flow and let
        // the user sidebar-jump past ConfigureToken.
        key={authType}
        title={
          <DialogTitleWithIcon text={spawnGeneral('title')}>
            <Gitlab size={24} />
          </DialogTitleWithIcon>
        }
        steps={steps}
        onCancel={onCancel}
        onComplete={onComplete}
        onStepViewed={onStepViewed}
        onStepNavigated={onStepNavigated}
        className="sm:max-w-250!"
        disableOutsideClick={true}
        disableEscapeKey={true}
        defaultFurthestStepIndex={defaultFurthestStepIndex}
      />
    </GitlabInstallationContext.Provider>
  );
};
